import React, { createContext, useContext, useEffect, useState } from 'react';

import { API } from '@/utils/API';

export interface PendingTerms {
  id: string;
  title: string;
  version: string;
  effective_date: string;
}

export interface UserData {
  id: string;
  cognito_sub: string;
  first_name: string;
  last_name: string;
  email_address: string;
  can_access_admin: boolean;
  /**
   * False while templates are managed in code (the
   * template_manual_editing_enabled flag is off) or for anyone who isn't an
   * IRB admin. The Form Builder renders read-only when false.
   */
  can_edit_submission_templates?: boolean;
  has_pending_terms: boolean;
  pending_terms?: PendingTerms;
  mfa_configured: boolean;
  /** False when login used Cognito's MFA_SETUP flow (SMS APIs need an access token). */
  mfa_sms_setup_available?: boolean;
  /**
   * True when the current session is an active Hub impersonation —
   * the MFA and terms gates let the session through when this is set,
   * because the whole point of impersonation is to debug what happens
   * after onboarding gates, not to re-encounter them.
   */
  impersonating: boolean;
  /** Answer Assist feature-family switches (Hub-controlled, default off). */
  features?: Record<string, boolean>;
  /**
   * True while the /api/v1 rate limits are switched off for a load-test run
   * (RACK_ATTACK_DISABLE_API_THROTTLES=1). Drives a persistent banner so the
   * environment cannot quietly stay unprotected once the run is over. Read at
   * boot on the server, so turning it back on needs a deploy/restart.
   */
  api_throttles_disabled?: boolean;
  /**
   * Gates the notification settings page (nav entry + route) — while false
   * the feature is fully dormant, not just disabled. Mirrors
   * NOTIFICATION_PERSONALIZATION_ENABLED, same pattern as mfa_configured.
   *
   * Required, not optional: UserBlueprint always sends it, and the settings
   * page treats a missing value as "off". Optional here would let the field
   * silently disappear from the payload and disable the page with no
   * compile-time complaint.
   */
  notification_personalization_enabled: boolean;
}

interface UserContextValue {
  userData: UserData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | null>(null);

const waitForSession = async (attempts = 0): Promise<boolean> => {
  if (attempts >= 10) return false;
  const res = await API.fetch('/session_status');
  const { authenticated } = await res.json();
  if (authenticated) return true;
  await new Promise(resolve => setTimeout(resolve, 300));
  return waitForSession(attempts + 1);
};

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserData = async (isRetry = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await API.authenticated.fetch('/users/me');

      if (response.ok) {
        const data = await response.json();
        setUserData(data);
      } else {
        const params = new URLSearchParams(window.location.search);
        if (!isRetry && params.get('impersonated') === 'true') {
          window.history.replaceState({}, '', window.location.pathname);
          const ready = await waitForSession();
          if (ready) return fetchUserData(true);
        }
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Failed to fetch user data');
      }
    } catch (e) {
      console.error('Failed to fetch user data:', e);
      setError('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const value: UserContextValue = {
    userData,
    loading,
    error,
    refetch: fetchUserData,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

/**
 * Like useUser but tolerates rendering outside a UserProvider (returns null) —
 * for opt-in features that must read as "off" rather than crash in isolated
 * render trees (tests, storybook, embedded fragments).
 */
export const useUserOptional = () => useContext(UserContext);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
